import { apiFetch } from "../../lib/api";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Loader2, Sparkles, Send, RefreshCw, Bot, User } from "lucide-react";
import { toast } from "sonner";
import { PriceChip } from "../common/PriceChip";
import { showCreditReceipt } from "../../lib/creditReceipt";

const SUGGESTED = [
  "Que producto vendi mas esta semana?",
  "Cual fue el mejor dia del mes?",
  "Que vendedor genero mas ingresos?",
  "A que hora se vende mas?",
  "Que mesa es la mas rentable?",
  "Compara las ventas de este mes con el anterior",
];

// ─── Extracted: Insights Card ─────────────────────────────────────────────────
function InsightsCard({ insights, loading, onGenerate, restaurantId }) {
  return (
    <Card data-testid="ai-insights-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-500" />
            Insights automaticos del periodo
            <PriceChip restaurantId={restaurantId} action="analytics_insights" />
          </CardTitle>
          <Button variant="outline" size="sm" onClick={onGenerate} disabled={loading} data-testid="generate-insights-btn" className="gap-1 text-xs">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {loading ? "Analizando..." : insights.length ? "Regenerar" : "Generar insights"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">Analisis narrativo generado por IA basado en tus datos reales</p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-3 py-4 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Analizando tus ventas...
          </div>
        ) : insights.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <Sparkles className="w-8 h-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">Haz clic en "Generar insights" para obtener un analisis narrativo de tus ventas</p>
          </div>
        ) : (
          <div className="space-y-3">
            {insights.map((insight, i) => (
              <div key={`insight-${i}`} className="flex gap-3 p-3 rounded-xl bg-muted/40 border border-border">
                <div className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</div>
                <p className="text-sm leading-relaxed">{insight}</p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Extracted: Chat Message ──────────────────────────────────────────────────
function ChatMessage({ m }) {
  return (
    <div className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
      {m.role === "assistant" && (
        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Bot className="w-3 h-3 text-primary" />
        </div>
      )}
      <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm leading-relaxed ${
        m.role === "user"
          ? "bg-primary text-primary-foreground rounded-br-sm"
          : "bg-background border border-border rounded-bl-sm"
      }`}>
        {m.content}
      </div>
      {m.role === "user" && (
        <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
          <User className="w-3 h-3" />
        </div>
      )}
    </div>
  );
}

// ─── Main AITab ───────────────────────────────────────────────────────────────
export function AITab({ restaurantId, startDate, endDate }) {
  const [insights, setInsights] = useState([]);
  const [insightsLoading, setIL] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [chatLoading, setCL] = useState(false);
  const [sessionId] = useState(() => `qa-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const bottomRef = useRef(null);
  const navigate = useNavigate();

  // Friendly toast with "Upgrade" + "Buy credits" CTAs when the IA gets throttled
  // (HTTP 429) OR when the restaurant runs out of credits (HTTP 422).
  const showPaywallToast = (detail, title = "Límite de IA alcanzado") => {
    toast.error(title, {
      description: detail || "Has alcanzado el límite de análisis IA. Actualiza tu plan o compra más créditos para seguir analizando.",
      duration: 10000,
      action: {
        label: "Ampliar plan",
        onClick: () => navigate("/app/subscription?tab=plans"),
      },
      cancel: {
        label: "Comprar créditos",
        onClick: () => navigate("/app/subscription?tab=credits"),
      },
    });
  };
  const showThrottleToast = showPaywallToast;  // back-compat name

  const loadInsights = async () => {
    if (!restaurantId) return;
    setIL(true);
    setInsights([]);
    try {
      const res = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/restaurants/${restaurantId}/analytics/ai-insights?start_date=${startDate || ""}&end_date=${endDate || ""}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ start_date: startDate, end_date: endDate }),
      });
      if (res.status === 429) {
        const data = await res.json().catch(() => ({}));
        showPaywallToast(data.detail, "Análisis IA bloqueado temporalmente");
        setIL(false);
        return;
      }
      if (res.status === 422) {
        const data = await res.json().catch(() => ({}));
        showPaywallToast(data.detail, "Créditos insuficientes");
        setIL(false);
        return;
      }
      const data = await res.json();
      setInsights(data.insights || []);
      showCreditReceipt(data._credits_receipt);
    } catch (e) { console.error(e); }
    setIL(false);
  };

  const sendMessage = async (text) => {
    if (!text.trim() || !restaurantId) return;
    setMessages(prev => [...prev, { role: "user", content: text, ts: new Date().toISOString() }]);
    setInput("");
    setCL(true);
    try {
      const res = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/restaurants/${restaurantId}/analytics/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ question: text, start_date: startDate, end_date: endDate, session_id: sessionId }),
      });
      if (res.status === 429) {
        const data = await res.json().catch(() => ({}));
        const msg = data.detail || "Has alcanzado el límite de análisis IA esta semana.";
        showPaywallToast(msg, "Asistente IA bloqueado temporalmente");
        setMessages(prev => [...prev, {
          role: "assistant",
          content: `${msg}\n\n💡 Puedes ampliar tu plan o comprar créditos desde la pantalla de Suscripción.`,
          ts: new Date().toISOString(),
        }]);
        setCL(false);
        return;
      }
      if (res.status === 422) {
        const data = await res.json().catch(() => ({}));
        const msg = data.detail || "Créditos insuficientes.";
        showPaywallToast(msg, "Créditos insuficientes");
        setMessages(prev => [...prev, {
          role: "assistant",
          content: `${msg}\n\n💡 Puedes comprar créditos o ampliar tu plan desde la pantalla de Suscripción.`,
          ts: new Date().toISOString(),
        }]);
        setCL(false);
        return;
      }
      const data = await res.json();
      setMessages(prev => [...prev, { role: "assistant", content: data.answer || data.detail || "Sin respuesta", ts: new Date().toISOString() }]);
      showCreditReceipt(data._credits_receipt);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Error al conectar con el asistente.", ts: new Date().toISOString() }]);
    }
    setCL(false);
  };

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  return (
    <div className="space-y-6">
      <InsightsCard insights={insights} loading={insightsLoading} onGenerate={loadInsights} restaurantId={restaurantId} />

      {/* Chat Q&A */}
      <Card data-testid="ai-chat-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Bot className="w-4 h-4 text-primary" /> Pregunta sobre tus datos
            <PriceChip restaurantId={restaurantId} action="analytics_ask" className="ml-auto" />
          </CardTitle>
          <p className="text-xs text-muted-foreground">Haz cualquier pregunta sobre las ventas del periodo seleccionado</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {messages.length === 0 && (
            <div className="flex flex-wrap gap-2">
              {SUGGESTED.map((s, i) => (
                <button key={`sug-${i}`} onClick={() => sendMessage(s)}
                  className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors text-muted-foreground hover:text-foreground">
                  {s}
                </button>
              ))}
            </div>
          )}
          {messages.length > 0 && (
            <div className="rounded-xl border border-border bg-muted/20 p-3 space-y-3 max-h-72 overflow-y-auto">
              {messages.map((m, i) => <ChatMessage key={`msg-${i}`} m={m} />)}
              {chatLoading && (
                <div className="flex gap-2 justify-start">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <Bot className="w-3 h-3 text-primary" />
                  </div>
                  <div className="px-3 py-2 rounded-xl bg-background border border-border">
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>
          )}
          <div className="flex gap-2">
            <input value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage(input)}
              placeholder="Cuanto vendi el fin de semana pasado?"
              className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              disabled={chatLoading} data-testid="ai-chat-input" />
            <Button onClick={() => sendMessage(input)} disabled={!input.trim() || chatLoading || !restaurantId} size="sm" data-testid="ai-chat-send">
              <Send className="w-3.5 h-3.5" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
